import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Linkedin, Facebook, Instagram, Youtube, Mail, Clock, Terminal, LogIn, User } from "lucide-react";

const Footer = () => {
  const { user, isAdmin, isClient } = useAuth();
  const year = new Date().getFullYear();
  const commandCenterHref = user ? (isAdmin ? "/admin" : "/portal") : "/login";
  const commandCenterLabel = user ? (isAdmin ? "Command Center" : "Client Portal") : "Command Center";
  // Dedicated Client Portal entry point — always visible in the footer so
  // brand owners never have to hunt for their dashboard login.
  const clientPortalHref = isClient ? "/portal" : "/login";
  return (
    <footer className="mt-24 bg-[var(--ft-black)] text-white ft-section-dark">
      <div className="ft-container py-20 relative">
        <div className="grid gap-14 md:grid-cols-4">
          {/* Brand */}
          <div>
            <span
              data-testid="footer-logo"
              className="font-bold tracking-[0.22em] text-[1.5rem] leading-none"
              style={{ color: "var(--ft-gold)", fontFamily: "Manrope, sans-serif" }}
            >
              FLOWTALIS<span className="text-[0.55em] align-super ml-0.5 font-medium">™</span>
            </span>
            <div className="ft-gold-rule-lg my-6" />
            <p className="max-w-xs text-sm leading-relaxed text-white/60">
              A premium private-label beverage manufacturing and brand development company.
            </p>
            <div className="mt-6 text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)]">
              From Source to Bottle™
            </div>
            {/* Social */}
            <div className="mt-8 flex items-center gap-3">
              {[
                { label: "LinkedIn", Icon: Linkedin },
                { label: "Facebook", Icon: Facebook },
                { label: "Instagram", Icon: Instagram },
                { label: "YouTube", Icon: Youtube },
              ].map(({ label, Icon }) => (
                <a
                  key={label}
                  href="#"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  className="h-11 w-11 rounded-full flex items-center justify-center border border-white/12 text-white/60 hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)] transition-all"
                >
                  <Icon size={16} strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          {/* Company */}
          <div>
            <div className="text-[0.6875rem] uppercase tracking-[0.28em] text-white/40 mb-5 font-semibold">Company</div>
            <ul className="space-y-1 text-sm text-white/70">
              <li><Link to="/about" className="block py-2 hover:text-[var(--ft-gold)] transition-colors">About</Link></li>
              <li><Link to="/services" className="block py-2 hover:text-[var(--ft-gold)] transition-colors">Services</Link></li>
              <li><Link to="/industries" className="block py-2 hover:text-[var(--ft-gold)] transition-colors">Industries</Link></li>
              <li><Link to="/contact" className="block py-2 hover:text-[var(--ft-gold)] transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <div className="text-[0.6875rem] uppercase tracking-[0.28em] text-white/40 mb-5 font-semibold">Resources</div>
            <ul className="space-y-1 text-sm text-white/70">
              <li><Link to="/request-samples" className="block py-2 hover:text-[var(--ft-gold)] transition-colors" data-testid="footer-samples">Request Samples</Link></li>
              <li><Link to="/trust" className="block py-2 hover:text-[var(--ft-gold)] transition-colors" data-testid="footer-trust">Trust &amp; Certifications</Link></li>
              <li><Link to="/faqs" className="block py-2 hover:text-[var(--ft-gold)] transition-colors">FAQ</Link></li>
              <li><Link to="/privacy" className="block py-2 hover:text-[var(--ft-gold)] transition-colors">Privacy Notice</Link></li>
              <li><Link to="/terms" className="block py-2 hover:text-[var(--ft-gold)] transition-colors">Terms &amp; Conditions</Link></li>
              <li><Link to="/refund" className="block py-2 hover:text-[var(--ft-gold)] transition-colors">Refund Policy</Link></li>
              <li><Link to="/security" className="block py-2 hover:text-[var(--ft-gold)] transition-colors">Security Policy</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="text-[0.6875rem] uppercase tracking-[0.28em] text-white/40 mb-5 font-semibold">Contact</div>
            <div className="space-y-4 text-sm">
              <div>
                <div className="flex items-center gap-2 text-[0.6875rem] uppercase tracking-[0.24em] text-[var(--ft-gold)] font-semibold">
                  <Mail size={12} strokeWidth={1.5} /> Sales &amp; Inquiries
                </div>
                <a href="mailto:hello@flowtalis.com" className="text-white/85 hover:text-[var(--ft-gold)] transition-colors mt-1.5 block">
                  hello@flowtalis.com
                </a>
              </div>
              <div>
                <div className="flex items-center gap-2 text-[0.6875rem] uppercase tracking-[0.24em] text-[var(--ft-gold)] font-semibold">
                  <Clock size={12} strokeWidth={1.5} /> Business Hours
                </div>
                <div className="text-white/85 mt-1.5">Monday — Friday</div>
                <div className="text-white/60 text-xs">9:00 AM — 5:00 PM EST</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[0.75rem] text-white/40">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <div>© {year} FLOWTALIS. All rights reserved.</div>
            <div className="hidden sm:inline text-white/20">·</div>
            <div>Manufactured with FDA-compliant partners across the United States.</div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 self-stretch sm:self-auto">
            <Link
              to={clientPortalHref}
              data-testid="footer-client-portal-btn"
              aria-label="Sign in to your Client Portal"
              className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ft-gold)] text-[var(--ft-black)] border border-[var(--ft-gold)] hover:bg-[var(--ft-gold)]/90 transition-all justify-center"
            >
              <User size={13} strokeWidth={1.75} />
              <span className="tracking-[0.18em] uppercase text-[0.7rem] font-semibold">Client Portal</span>
              <LogIn size={12} strokeWidth={2} className="opacity-70 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to={commandCenterHref}
              data-testid="footer-command-center-btn"
              aria-label="Command Center login"
              className="group inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--ft-gold)]/40 text-[var(--ft-gold)] hover:bg-[var(--ft-gold)] hover:text-[var(--ft-black)] hover:border-[var(--ft-gold)] transition-all justify-center"
            >
              <Terminal size={13} strokeWidth={1.75} />
              <span className="tracking-[0.18em] uppercase text-[0.7rem] font-semibold">{commandCenterLabel}</span>
              <LogIn size={12} strokeWidth={2} className="opacity-70 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
